<template>
  <section class="fm-root">
    <!-- Toolbar -->
    <div class="fm-toolbar">
      <div class="fm-breadcrumb">
        <button
          class="fm-crumb"
          :class="{ 'fm-crumb-active': !currentFolderId, 'fm-crumb-drop': dragOverRoot }"
          @click="exitFolder"
          @dragover.prevent="onRootDragOver"
          @dragleave.prevent="onRootDragLeave"
          @drop.prevent="onRootDrop"
        >
          Arquivos
        </button>
        <template v-if="currentFolder">
          <span class="fm-crumb-sep">›</span>
          <span class="fm-crumb fm-crumb-active">{{ currentFolder.name }}</span>
        </template>
      </div>
      <div class="fm-toolbar-actions">
        <span class="fm-count">{{ visibleFiles.length }} arquivo{{ visibleFiles.length !== 1 ? 's' : '' }}</span>
        <template v-if="accessMode === 'edit' && hasSelectedFiles">
          <div class="fm-selection-bar">
            <span class="fm-selection-count">{{ selectedFiles.length }} selecionado{{ selectedFiles.length !== 1 ? 's' : '' }}</span>
            <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="selectAllVisibleFiles">Marcar visÃ­veis</button>
            <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="openMoveModal">Mover</button>
            <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="duplicateSelectedFiles" :disabled="bulkActionLoading">Duplicar</button>
            <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="openRenameSelected" :disabled="selectedFiles.length !== 1">Renomear</button>
            <button class="fm-btn fm-btn-danger fm-btn-sm" type="button" @click="askDeleteSelected" :disabled="bulkActionLoading">Apagar</button>
            <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="clearSelection">Limpar</button>
          </div>
        </template>
        <template v-if="accessMode === 'edit'">
          <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="startCreateFolder">
            + Pasta
          </button>
          <button class="fm-btn fm-btn-primary fm-btn-sm" type="button" @click="toggleUploadPanel">
            + Arquivo
          </button>
        </template>
      </div>
    </div>

    <!-- Create folder inline -->
    <Transition name="slide-down">
      <div v-if="creatingFolder" class="fm-create-folder">
        <input
          ref="folderInputRef"
          v-model="newFolderName"
          class="fm-folder-input"
          type="text"
          placeholder="Nome da pasta"
          @keydown.enter="submitCreateFolder"
          @keydown.esc="creatingFolder = false"
        />
        <button class="fm-btn fm-btn-primary fm-btn-sm" type="button" @click="submitCreateFolder">Criar</button>
        <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="creatingFolder = false">Cancelar</button>
      </div>
    </Transition>

    <!-- Upload Panel -->
    <Transition name="slide-down">
      <div v-if="showCreateForm" class="fm-upload-panel">
        <div class="fm-panel-inner">
          <div class="fm-panel-header">
            <span class="fm-panel-title">Adicionar arquivo(s)</span>
            <button class="fm-close-btn" type="button" @click="toggleCreateForm">✕</button>
          </div>
          <form @submit.prevent="submitCreate">
            <div
              class="fm-drop-zone"
              :class="{ 'fm-drop-zone--has-file': createForm.selectedFileName }"
              @click="triggerNewFile"
              @dragover.prevent
              @drop.prevent="onDropFile"
            >
              <input ref="newFileInputRef" type="file" accept=".pdf,.dwg,image/*" class="hidden-input" @change="onNewFilePicked" />
              <div class="fm-drop-icon">{{ createForm.selectedFileName ? '✔' : '↑' }}</div>
              <div class="fm-drop-label">{{ createForm.selectedFileName || 'Clique ou arraste um arquivo' }}</div>
              <div class="fm-drop-hint">PDF, DWG ou imagem</div>
            </div>
            <div class="fm-form-row">
              <div class="fm-field">
                <label>Nome no sistema</label>
                <input v-model="createForm.customName" type="text" placeholder="Ex: Planta baixa R0" />
              </div>
              <div class="fm-field fm-field-xs">
                <label>Revisão</label>
                <select v-model="createForm.revision">
                  <option v-for="r in revisionOptions" :key="r" :value="r">{{ r }}</option>
                </select>
              </div>
            </div>
            <div class="fm-field">
              <label>Descrição</label>
              <textarea v-model="createForm.description" rows="2" placeholder="Opcional" />
            </div>
            <div v-if="folders.length > 0" class="fm-field fm-field-xs">
              <label>Pasta</label>
              <select v-model="createForm.folderId">
                <option value="">Raiz</option>
                <option v-for="f in folders" :key="f.id" :value="f.id">{{ f.name }}</option>
              </select>
            </div>
            <div class="fm-panel-footer">
              <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="triggerMultipleFiles">
                <input ref="multiFileInputRef" type="file" accept=".pdf,.dwg,image/*" multiple class="hidden-input" @change="onMultipleFilesPicked" />
                Múltiplos arquivos{{ multiFiles.length > 0 ? ` (${multiFiles.length})` : '' }}
              </button>
              <div class="fm-panel-btns">
                <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="toggleCreateForm">Cancelar</button>
                <button class="fm-btn fm-btn-primary fm-btn-sm" type="submit" :disabled="uploading">
                  {{ uploading ? 'Enviando…' : 'Enviar' }}
                </button>
              </div>
            </div>
            <p v-if="formError" class="fm-error">{{ formError }}</p>
          </form>
        </div>
      </div>
    </Transition>

    <!-- Grid -->
    <div v-if="gridItems.length === 0 && !showCreateForm" class="fm-empty">
      <div class="fm-empty-icon">{{ currentFolderId ? '📂' : '📁' }}</div>
      <p>{{ currentFolderId ? 'Esta pasta está vazia.' : 'Nenhum arquivo ainda.' }}</p>
      <button v-if="accessMode === 'edit' && !currentFolderId" class="fm-btn fm-btn-primary fm-btn-sm" type="button" @click="toggleUploadPanel">
        Adicionar arquivo
      </button>
    </div>

    <div v-else-if="gridItems.length > 0" class="fm-grid">
      <!-- Folder cards -->
      <div
        v-for="folder in visibleFolders"
        :key="'f-' + folder.id"
        class="fm-card fm-card--folder"
        :class="{ 'fm-card--drag-over': dragOverFolderId === folder.id }"
        @click="enterFolder(folder)"
        @dragover.prevent="onFolderDragOver(folder)"
        @dragleave.prevent="onFolderDragLeave(folder)"
        @drop.prevent="onFolderDrop(folder)"
      >
        <div class="fm-card-thumb fm-thumb-folder">
          <svg width="38" height="30" viewBox="0 0 38 30" fill="none">
            <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.25"/>
            <rect x="0" y="3" width="16" height="6" rx="2" fill="currentColor" opacity="0.4"/>
            <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.15"/>
          </svg>
        </div>
        <div class="fm-card-info">
          <div class="fm-card-name" :title="folder.name">{{ folder.name }}</div>
          <div class="fm-card-meta">
            <span class="fm-card-type">Pasta</span>
          </div>
        </div>
        <div v-if="accessMode === 'edit'" class="fm-card-hover" @click.stop>
          <button class="fm-action-btn" title="Renomear" @click="openRenameFolder(folder)">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5a1.5 1.5 0 012 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" stroke-width="1.3" fill="none"/></svg>
          </button>
          <button class="fm-action-btn fm-action-danger" title="Excluir" @click="askDeleteFolder(folder)">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V2h4v2M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4H5z" stroke="currentColor" stroke-width="1.3" fill="none"/></svg>
          </button>
        </div>
      </div>

      <!-- File cards -->
      <div
        v-for="item in visibleFiles"
        :key="'file-' + item.id"
        class="fm-card fm-card--file"
        :class="{ 'fm-card--selected': isFileSelected(item.id), 'fm-card--dragging': draggingFileIds.includes(item.id) }"
        :draggable="accessMode === 'edit'"
        @click="openViewer(item)"
        @dragstart="onFileDragStart(item, $event)"
        @dragend="onFileDragEnd"
      >
        <label
          v-if="accessMode === 'edit'"
          class="fm-select-check"
          :class="{ 'fm-select-check--checked': isFileSelected(item.id) }"
          @click.stop
        >
          <input
            type="checkbox"
            :checked="isFileSelected(item.id)"
            @change="toggleFileSelection(item.id)"
          />
          <span />
        </label>
        <!-- Thumbnail or icon -->
        <div class="fm-card-thumb">
          <img
            v-if="item.thumbnailBase64"
            class="fm-thumb-img"
            :src="item.thumbnailBase64.startsWith('/uploads/') ? $resolveUrl(item.thumbnailBase64) : `data:image/jpeg;base64,${item.thumbnailBase64}`"
            alt=""
          />
          <div v-else class="fm-file-icon" :class="`fm-icon-${item.extension.toLowerCase()}`">
            <span class="fm-icon-ext">{{ item.extension.toUpperCase() }}</span>
          </div>
        </div>
        <div class="fm-card-info">
          <div class="fm-card-name" :title="item.name">{{ item.name }}</div>
          <div class="fm-card-meta">
            <span class="fm-badge-rev">{{ item.revision || 'R0' }}</span>
            <span class="fm-card-type">{{ item.extension.toUpperCase() }}</span>
          </div>
        </div>
        <div class="fm-card-hover" @click.stop>
          <button class="fm-action-btn" title="Visualizar" @click="openViewer(item)">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3C4.5 3 1.5 8 1.5 8s3 5 6.5 5 6.5-5 6.5-5-3-5-6.5-5zm0 8a3 3 0 110-6 3 3 0 010 6z" fill="currentColor"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/></svg>
          </button>
          <button v-if="accessMode === 'edit'" class="fm-action-btn" title="Editar" @click="openEditForm(item.id)">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5a1.5 1.5 0 012 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" stroke-width="1.3" fill="none"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit drawer -->
    <Transition name="slide-down">
      <div v-if="editingId && editingItem" class="fm-upload-panel fm-edit-panel">
        <div class="fm-panel-inner">
          <div class="fm-panel-header">
            <span class="fm-panel-title">Editar: {{ editingItem.name }}</span>
            <button class="fm-close-btn" type="button" @click="cancelEdit">✕</button>
          </div>
          <form @submit.prevent="submitEdit(editingItem)">
            <div class="fm-drop-zone fm-drop-zone--sm" @click="triggerEditFile">
              <input ref="editFileInputRef" type="file" accept=".pdf,.dwg,image/*" class="hidden-input" @change="onEditFilePicked" />
              <div class="fm-drop-icon">{{ editForm.selectedFileName ? '✔' : '↑' }}</div>
              <div class="fm-drop-label">{{ editForm.selectedFileName || 'Substituir arquivo (opcional)' }}</div>
            </div>
            <div class="fm-form-row">
              <div class="fm-field">
                <label>Nome</label>
                <input v-model="editForm.customName" type="text" placeholder="Nome do arquivo" />
              </div>
              <div class="fm-field fm-field-xs">
                <label>Revisão</label>
                <select v-model="editForm.revision">
                  <option v-for="r in revisionOptions" :key="`e-${r}`" :value="r">{{ r }}</option>
                </select>
              </div>
            </div>
            <div class="fm-field">
              <label>Descrição</label>
              <textarea v-model="editForm.description" rows="2" placeholder="Opcional" />
            </div>
            <div v-if="folders.length > 0" class="fm-field fm-field-xs">
              <label>Pasta</label>
              <select v-model="editForm.folderId">
                <option value="">Raiz</option>
                <option v-for="f in folders" :key="f.id" :value="f.id">{{ f.name }}</option>
              </select>
            </div>
            <div class="fm-panel-footer">
              <button class="fm-btn fm-btn-danger fm-btn-sm" type="button" @click="askDelete(editingItem)">Excluir</button>
              <div class="fm-panel-btns">
                <button class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="cancelEdit">Cancelar</button>
                <button class="fm-btn fm-btn-primary fm-btn-sm" type="submit">Salvar</button>
              </div>
            </div>
            <p v-if="formError" class="fm-error">{{ formError }}</p>
          </form>
        </div>
      </div>
    </Transition>

    <!-- Rename folder modal -->
    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="renamingFolder" class="fm-viewer-overlay" @click.self="renamingFolder = null">
          <div class="fm-rename-modal">
            <div class="fm-panel-header">
              <span class="fm-panel-title">Renomear pasta</span>
              <button class="fm-close-btn" @click="renamingFolder = null">✕</button>
            </div>
            <input
              ref="renameInputRef"
              v-model="renameFolderName"
              class="fm-folder-input"
              type="text"
              placeholder="Novo nome"
              @keydown.enter="submitRenameFolder"
              @keydown.esc="renamingFolder = null"
            />
            <div class="fm-panel-btns" style="margin-top:12px">
              <button class="fm-btn fm-btn-ghost fm-btn-sm" @click="renamingFolder = null">Cancelar</button>
              <button class="fm-btn fm-btn-primary fm-btn-sm" @click="submitRenameFolder">Salvar</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="showMoveModal" class="fm-viewer-overlay" @click.self="closeMoveModal">
          <div class="fm-rename-modal">
            <div class="fm-panel-header">
              <span class="fm-panel-title">Mover {{ selectedFiles.length }} arquivo{{ selectedFiles.length !== 1 ? 's' : '' }}</span>
              <button class="fm-close-btn" @click="closeMoveModal">âœ•</button>
            </div>
            <div class="fm-field">
              <label>Destino</label>
              <select v-model="moveTargetFolderId">
                <option value="">Raiz</option>
                <option v-for="f in folders" :key="`move-${f.id}`" :value="f.id">{{ f.name }}</option>
              </select>
            </div>
            <div class="fm-panel-btns" style="margin-top:12px">
              <button class="fm-btn fm-btn-ghost fm-btn-sm" @click="closeMoveModal">Cancelar</button>
              <button class="fm-btn fm-btn-primary fm-btn-sm" @click="submitMoveSelected" :disabled="bulkActionLoading">Mover</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="showRenameSelectionModal" class="fm-viewer-overlay" @click.self="closeRenameSelection">
          <div class="fm-rename-modal">
            <div class="fm-panel-header">
              <span class="fm-panel-title">Renomear arquivo</span>
              <button class="fm-close-btn" @click="closeRenameSelection">âœ•</button>
            </div>
            <input
              ref="renameSelectionInputRef"
              v-model="renameSelectionName"
              class="fm-folder-input"
              type="text"
              placeholder="Novo nome do arquivo"
              @keydown.enter="submitRenameSelected"
              @keydown.esc="closeRenameSelection"
            />
            <div class="fm-panel-btns" style="margin-top:12px">
              <button class="fm-btn fm-btn-ghost fm-btn-sm" @click="closeRenameSelection">Cancelar</button>
              <button class="fm-btn fm-btn-primary fm-btn-sm" @click="submitRenameSelected" :disabled="bulkActionLoading">Salvar</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Viewer Modal -->
    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="viewerFile" class="fm-viewer-overlay" @click.self="closeViewer">
          <div class="fm-viewer-modal">
            <div class="fm-viewer-header">
              <div class="fm-viewer-title-group">
                <div class="fm-file-icon fm-file-icon-sm" :class="`fm-icon-${viewerFile.extension.toLowerCase()}`">
                  <span class="fm-icon-ext">{{ viewerFile.extension.toUpperCase() }}</span>
                </div>
                <div>
                  <div class="fm-viewer-name">{{ viewerFile.name }}</div>
                  <div class="fm-viewer-meta">{{ viewerFile.extension.toUpperCase() }} · {{ viewerFile.revision || 'R0' }}{{ viewerFile.description ? ' · ' + viewerFile.description : '' }}</div>
                </div>
              </div>
              <div class="fm-viewer-controls">
                <button v-if="accessMode === 'edit'" class="fm-btn fm-btn-ghost fm-btn-sm" type="button" @click="openEditFromViewer">Editar</button>
                <button class="fm-viewer-close" type="button" @click="closeViewer">✕</button>
              </div>
            </div>
            <div class="fm-viewer-body">
              <iframe
                v-if="viewerFile.extension === 'pdf'"
                class="fm-iframe"
                :src="viewerFile.url ? $resolveUrl(viewerFile.url) : `data:${viewerFile.mimeType};base64,${viewerFile.contentBase64}`"
              />
              <div v-else-if="viewerFile.extension === 'dwg'" class="fm-dwg-placeholder">
                <div class="fm-dwg-big">📐</div>
                <p>Arquivo DWG</p>
                <p class="fm-viewer-meta">Visualização DWG não disponível no navegador.</p>
              </div>
              <img
                v-else
                class="fm-img-viewer"
                :src="viewerFile.url ? $resolveUrl(viewerFile.url) : `data:${viewerFile.mimeType};base64,${viewerFile.contentBase64}`"
                alt="preview"
              />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <ConfirmDeleteModal :open="confirmOpen" :label="confirmLabel" @confirm="confirmDelete" @cancel="cancelDelete" />
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue'
import { apiFetch, getApiBase, getAuthToken } from '@/store/api'
import ConfirmDeleteModal from '@/components/dashboard/ConfirmDeleteModal.vue'

interface ProjectFile {
  id: string
  name: string
  description?: string
  revision?: string
  mimeType: string
  extension: string
  contentBase64?: string
  url?: string
  folderId?: string | null
  thumbnailBase64?: string | null
}

interface ProjectFolder {
  id: string
  name: string
  createdAt: string
}

// ── Thumbnail generation ──────────────────────────────────────

async function generateImageThumbnail(file: File): Promise<string> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      try {
        const MAX = 280
        const ratio = Math.min(MAX / img.naturalWidth, MAX / img.naturalHeight, 1)
        const w = Math.max(1, Math.round(img.naturalWidth * ratio))
        const h = Math.max(1, Math.round(img.naturalHeight * ratio))
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) { resolve(''); return }
        ctx.drawImage(img, 0, 0, w, h)
        resolve(canvas.toDataURL('image/jpeg', 0.7).split(',')[1] ?? '')
      } finally {
        URL.revokeObjectURL(url)
      }
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve('') }
    img.src = url
  })
}

async function generatePdfThumbnail(file: File): Promise<string> {
  try {
    const pdfjsLib = await import('pdfjs-dist')
    // Use CDN worker (reliable for upload-time use)
    ;(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
      `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`
    const arrayBuffer = await file.arrayBuffer()
    const pdf = await (pdfjsLib as any).getDocument({ data: arrayBuffer }).promise
    const page = await pdf.getPage(1)
    const viewport = page.getViewport({ scale: 0.6 })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    await page.render({ canvasContext: ctx, viewport }).promise
    return canvas.toDataURL('image/jpeg', 0.7).split(',')[1] ?? ''
  } catch {
    return ''
  }
}

async function generateThumbnail(file: File): Promise<string> {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase()
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return generateImageThumbnail(file)
  }
  if (ext === 'pdf') {
    return generatePdfThumbnail(file)
  }
  return ''
}

export default defineComponent({
  name: 'ProjectFilesSection',
  components: { ConfirmDeleteModal },
  props: {
    projectId: { type: String, required: true },
    accessMode: { type: String as () => 'read' | 'edit', default: 'read' }
  },
  setup(props) {
    const files = ref<ProjectFile[]>([])
    const folders = ref<ProjectFolder[]>([])
    const currentFolderId = ref<string | null>(null)
    const viewerFile = ref<ProjectFile | null>(null)
    const showCreateForm = ref(false)
    const uploading = ref(false)
    const formError = ref('')
    const editingId = ref('')
    const confirmOpen = ref(false)
    const confirmLabel = ref('')
    const pendingDeleteId = ref('')
    const pendingDeleteType = ref<'file' | 'folder'>('file')
    const creatingFolder = ref(false)
    const newFolderName = ref('')
    const renamingFolder = ref<ProjectFolder | null>(null)
    const renameFolderName = ref('')
    const selectedFileIds = ref<string[]>([])
    const dragOverFolderId = ref('')
    const dragOverRoot = ref(false)
    const draggingFileIds = ref<string[]>([])
    const showMoveModal = ref(false)
    const moveTargetFolderId = ref('')
    const showRenameSelectionModal = ref(false)
    const renameSelectionName = ref('')
    const bulkActionLoading = ref(false)
    const pendingDeleteIds = ref<string[]>([])
    const pendingDeleteKind = ref<'single' | 'selection'>('single')
    const folderInputRef = ref<HTMLInputElement | null>(null)
    const renameInputRef = ref<HTMLInputElement | null>(null)
    const renameSelectionInputRef = ref<HTMLInputElement | null>(null)
    const newFileInputRef = ref<HTMLInputElement | null>(null)
    const multiFileInputRef = ref<HTMLInputElement | null>(null)
    const editFileInputRef = ref<HTMLInputElement | null>(null)
    const createFile = ref<File | null>(null)
    const editFile = ref<File | null>(null)
    const multiFiles = ref<File[]>([])
    const revisionOptions = ['R0', 'R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R8', 'R9']

    const createForm = ref({ selectedFileName: '', customName: '', description: '', revision: 'R0', folderId: '' })
    const editForm = ref({ selectedFileName: '', customName: '', description: '', revision: 'R0', folderId: '' })

    const currentFolder = computed(() => folders.value.find((f) => f.id === currentFolderId.value) ?? null)
    const editingItem = computed(() => files.value.find((f) => f.id === editingId.value) ?? null)
    const selectedFiles = computed(() => files.value.filter((file) => selectedFileIds.value.includes(file.id)))
    const hasSelectedFiles = computed(() => selectedFiles.value.length > 0)
    const selectedSingleFile = computed(() => selectedFiles.value[0] ?? null)

    const visibleFolders = computed(() =>
      currentFolderId.value ? [] : folders.value
    )

    const visibleFiles = computed(() =>
      files.value.filter((f) =>
        currentFolderId.value
          ? f.folderId === currentFolderId.value
          : !f.folderId
      )
    )

    const gridItems = computed(() => [...visibleFolders.value, ...visibleFiles.value])

    const refreshFiles = async () => {
      if (!props.projectId) return
      const data = await apiFetch<{ files: ProjectFile[]; folders: ProjectFolder[] }>(
        `/projects/${encodeURIComponent(props.projectId)}/files`
      )
      files.value = data.files ?? []
      folders.value = data.folders ?? []
      selectedFileIds.value = selectedFileIds.value.filter((id) => files.value.some((file) => file.id === id))
    }

    const toBase64 = (file: Blob) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result !== 'string') { reject(new Error('Falha ao ler arquivo.')); return }
          resolve(reader.result.split(',')[1] || '')
        }
        reader.onerror = () => reject(new Error('Falha ao ler arquivo.'))
        reader.readAsDataURL(file)
      })

    const fetchFileBase64 = async (item: ProjectFile) => {
      const payload = await apiFetch<ProjectFile>(`/project-files/${encodeURIComponent(item.id)}/content`)
      if (payload.contentBase64) return payload.contentBase64
      if (!payload.url) throw new Error('Nao foi possivel carregar o conteudo do arquivo.')
      const token = getAuthToken()
      const response = await fetch(`${getApiBase()}${payload.url}`, {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      if (!response.ok) throw new Error('Falha ao baixar arquivo para duplicacao.')
      return toBase64(await response.blob())
    }

    const isFileSelected = (id: string) => selectedFileIds.value.includes(id)

    const clearSelection = () => {
      selectedFileIds.value = []
      draggingFileIds.value = []
      dragOverFolderId.value = ''
      dragOverRoot.value = false
    }

    const toggleFileSelection = (id: string) => {
      if (isFileSelected(id)) {
        selectedFileIds.value = selectedFileIds.value.filter((fileId) => fileId !== id)
      } else {
        selectedFileIds.value = [...selectedFileIds.value, id]
      }
    }

    const selectAllVisibleFiles = () => {
      selectedFileIds.value = Array.from(new Set([...selectedFileIds.value, ...visibleFiles.value.map((file) => file.id)]))
    }

    const moveFilesToFolder = async (fileIds: string[], folderId: string | null) => {
      const uniqueIds = Array.from(new Set(fileIds))
      if (!uniqueIds.length) return
      bulkActionLoading.value = true
      try {
        await Promise.all(
          uniqueIds.map((fileId) =>
            apiFetch(`/project-files/${encodeURIComponent(fileId)}`, {
              method: 'PUT',
              body: JSON.stringify({ folderId })
            })
          )
        )
        if (currentFolderId.value && folderId !== currentFolderId.value) {
          selectedFileIds.value = selectedFileIds.value.filter((id) => !uniqueIds.includes(id))
        }
        await refreshFiles()
      } finally {
        bulkActionLoading.value = false
        draggingFileIds.value = []
        dragOverFolderId.value = ''
        dragOverRoot.value = false
      }
    }

    const resetCreateForm = () => {
      createForm.value = { selectedFileName: '', customName: '', description: '', revision: 'R0', folderId: currentFolderId.value ?? '' }
      createFile.value = null
      multiFiles.value = []
    }

    // ── Upload panel ─────────────────────────────────────────

    const toggleCreateForm = () => {
      showCreateForm.value = !showCreateForm.value
      formError.value = ''
      editingId.value = ''
      if (!showCreateForm.value) resetCreateForm()
    }

    const toggleUploadPanel = () => {
      editingId.value = ''
      createForm.value.folderId = currentFolderId.value ?? ''
      toggleCreateForm()
    }

    const triggerNewFile = () => newFileInputRef.value?.click()
    const triggerEditFile = () => editFileInputRef.value?.click()
    const triggerMultipleFiles = () => multiFileInputRef.value?.click()

    const onNewFilePicked = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0] || null
      createFile.value = file
      createForm.value.selectedFileName = file?.name || ''
      if (file && !createForm.value.customName) createForm.value.customName = file.name.replace(/\.[^.]+$/, '')
      multiFiles.value = []
    }

    const onDropFile = (event: DragEvent) => {
      const file = event.dataTransfer?.files?.[0] || null
      if (!file) return
      createFile.value = file
      createForm.value.selectedFileName = file.name
      if (!createForm.value.customName) createForm.value.customName = file.name.replace(/\.[^.]+$/, '')
      multiFiles.value = []
    }

    const onMultipleFilesPicked = (event: Event) => {
      multiFiles.value = Array.from((event.target as HTMLInputElement).files || [])
      createFile.value = null
      createForm.value.selectedFileName =
        multiFiles.value.length > 0 ? `${multiFiles.value.length} arquivo(s) selecionado(s)` : ''
    }

    const onEditFilePicked = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0] || null
      editFile.value = file
      editForm.value.selectedFileName = file?.name || ''
    }

    const submitCreate = async () => {
      formError.value = ''
      uploading.value = true
      try {
        const targetFolderId = createForm.value.folderId || null

        if (multiFiles.value.length > 0) {
          for (const file of multiFiles.value) {
            const contentBase64 = await toBase64(file)
            const thumbnailBase64 = await generateThumbnail(file)
            await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/files`, {
              method: 'POST',
              body: JSON.stringify({
                name: file.name, description: '', revision: createForm.value.revision,
                mimeType: file.type || 'application/octet-stream', contentBase64,
                folderId: targetFolderId, thumbnailBase64: thumbnailBase64 || null
              })
            })
          }
          multiFiles.value = []
          showCreateForm.value = false
          resetCreateForm()
          await refreshFiles()
          return
        }

        if (!createFile.value) { formError.value = 'Selecione um arquivo.'; return }
        const ext = createFile.value.name.includes('.') ? createFile.value.name.split('.').pop() || '' : ''
        let name = (createForm.value.customName || createFile.value.name).trim()
        if (name && !name.includes('.') && ext) name = `${name}.${ext}`
        if (!name) { formError.value = 'Informe o nome do arquivo.'; return }
        const contentBase64 = await toBase64(createFile.value)
        const thumbnailBase64 = await generateThumbnail(createFile.value)
        await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/files`, {
          method: 'POST',
          body: JSON.stringify({
            name, description: createForm.value.description, revision: createForm.value.revision,
            mimeType: createFile.value.type || 'application/octet-stream', contentBase64,
            folderId: targetFolderId, thumbnailBase64: thumbnailBase64 || null
          })
        })
        showCreateForm.value = false
        resetCreateForm()
        await refreshFiles()
      } finally {
        uploading.value = false
      }
    }

    // ── Viewer ────────────────────────────────────────────────

    const openViewer = async (item: ProjectFile) => {
      editingId.value = ''
      showCreateForm.value = false
      viewerFile.value = await apiFetch<ProjectFile>(`/project-files/${encodeURIComponent(item.id)}/content`)
    }

    const closeViewer = () => { viewerFile.value = null }

    const openEditFromViewer = () => {
      if (!viewerFile.value) return
      const id = viewerFile.value.id
      closeViewer()
      openEditForm(id)
    }

    // ── Edit ──────────────────────────────────────────────────

    const openEditForm = (id: string) => {
      const item = files.value.find((f) => f.id === id)
      if (!item) return
      editingId.value = id
      showCreateForm.value = false
      formError.value = ''
      editFile.value = null
      editForm.value = {
        selectedFileName: '', customName: item.name || '', description: item.description || '',
        revision: item.revision || 'R0', folderId: item.folderId || ''
      }
    }

    const cancelEdit = () => { editingId.value = ''; editFile.value = null; formError.value = '' }

    const submitEdit = async (item: ProjectFile) => {
      formError.value = ''
      const baseName = editFile.value?.name || item.name
      const ext = baseName.includes('.') ? baseName.split('.').pop() || '' : ''
      let name = (editForm.value.customName || baseName).trim()
      if (name && !name.includes('.') && ext) name = `${name}.${ext}`
      if (!name) { formError.value = 'Informe o nome do arquivo.'; return }
      const payload: Record<string, unknown> = {
        name, description: editForm.value.description, revision: editForm.value.revision,
        folderId: editForm.value.folderId || null
      }
      if (editFile.value) {
        payload.mimeType = editFile.value.type || 'application/octet-stream'
        payload.contentBase64 = await toBase64(editFile.value)
        payload.thumbnailBase64 = (await generateThumbnail(editFile.value)) || null
      }
      await apiFetch(`/project-files/${encodeURIComponent(item.id)}`, { method: 'PUT', body: JSON.stringify(payload) })
      editingId.value = ''
      editFile.value = null
      await refreshFiles()
    }

    // ── Folders ───────────────────────────────────────────────

    const enterFolder = (folder: ProjectFolder) => {
      currentFolderId.value = folder.id
      editingId.value = ''
      showCreateForm.value = false
    }

    const exitFolder = () => { currentFolderId.value = null }

    const openMoveModal = () => {
      if (!hasSelectedFiles.value) return
      moveTargetFolderId.value = currentFolderId.value ?? ''
      showMoveModal.value = true
    }

    const closeMoveModal = () => {
      showMoveModal.value = false
      moveTargetFolderId.value = ''
    }

    const submitMoveSelected = async () => {
      await moveFilesToFolder(selectedFileIds.value, moveTargetFolderId.value || null)
      closeMoveModal()
    }

    const openRenameSelected = async () => {
      if (selectedFiles.value.length !== 1 || !selectedSingleFile.value) return
      renameSelectionName.value = selectedSingleFile.value.name
      showRenameSelectionModal.value = true
      await nextTick()
      renameSelectionInputRef.value?.focus()
    }

    const closeRenameSelection = () => {
      showRenameSelectionModal.value = false
      renameSelectionName.value = ''
    }

    const submitRenameSelected = async () => {
      if (!selectedSingleFile.value) return
      const name = renameSelectionName.value.trim()
      if (!name) return
      bulkActionLoading.value = true
      try {
        await apiFetch(`/project-files/${encodeURIComponent(selectedSingleFile.value.id)}`, {
          method: 'PUT',
          body: JSON.stringify({ name })
        })
        closeRenameSelection()
        await refreshFiles()
      } finally {
        bulkActionLoading.value = false
      }
    }

    const duplicateSelectedFiles = async () => {
      if (!hasSelectedFiles.value) return
      bulkActionLoading.value = true
      try {
        for (const item of selectedFiles.value) {
          const contentBase64 = await fetchFileBase64(item)
          const dotIndex = item.name.lastIndexOf('.')
          const copyName = dotIndex > 0
            ? `${item.name.slice(0, dotIndex)} - copia${item.name.slice(dotIndex)}`
            : `${item.name} - copia`
          await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/files`, {
            method: 'POST',
            body: JSON.stringify({
              name: copyName,
              description: item.description || '',
              revision: item.revision || 'R0',
              mimeType: item.mimeType,
              contentBase64,
              folderId: item.folderId || null,
              thumbnailBase64: item.thumbnailBase64 || null
            })
          })
        }
        await refreshFiles()
      } finally {
        bulkActionLoading.value = false
      }
    }

    const onFileDragStart = (item: ProjectFile, event: DragEvent) => {
      if (props.accessMode !== 'edit') return
      const ids = isFileSelected(item.id) ? selectedFileIds.value : [item.id]
      if (!isFileSelected(item.id)) selectedFileIds.value = [item.id]
      draggingFileIds.value = ids
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', ids.join(','))
      }
    }

    const onFileDragEnd = () => {
      draggingFileIds.value = []
      dragOverFolderId.value = ''
      dragOverRoot.value = false
    }

    const onFolderDragOver = (folder: ProjectFolder) => {
      if (!draggingFileIds.value.length) return
      dragOverFolderId.value = folder.id
    }

    const onFolderDragLeave = (folder: ProjectFolder) => {
      if (dragOverFolderId.value === folder.id) dragOverFolderId.value = ''
    }

    const onFolderDrop = async (folder: ProjectFolder) => {
      const fileIds = draggingFileIds.value.length ? draggingFileIds.value : selectedFileIds.value
      if (!fileIds.length) return
      await moveFilesToFolder(fileIds, folder.id)
    }

    const onRootDragOver = () => {
      if (!draggingFileIds.value.length) return
      dragOverRoot.value = true
    }

    const onRootDragLeave = () => {
      dragOverRoot.value = false
    }

    const onRootDrop = async () => {
      const fileIds = draggingFileIds.value.length ? draggingFileIds.value : selectedFileIds.value
      if (!fileIds.length) return
      await moveFilesToFolder(fileIds, null)
    }

    const startCreateFolder = async () => {
      creatingFolder.value = true
      newFolderName.value = ''
      await nextTick()
      folderInputRef.value?.focus()
    }

    const submitCreateFolder = async () => {
      const name = newFolderName.value.trim()
      if (!name) return
      await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/folders`, {
        method: 'POST', body: JSON.stringify({ name })
      })
      creatingFolder.value = false
      newFolderName.value = ''
      await refreshFiles()
    }

    const openRenameFolder = async (folder: ProjectFolder) => {
      renamingFolder.value = folder
      renameFolderName.value = folder.name
      await nextTick()
      renameInputRef.value?.focus()
    }

    const submitRenameFolder = async () => {
      if (!renamingFolder.value) return
      const name = renameFolderName.value.trim()
      if (!name) return
      await apiFetch(`/project-folders/${encodeURIComponent(renamingFolder.value.id)}`, {
        method: 'PUT', body: JSON.stringify({ name })
      })
      renamingFolder.value = null
      await refreshFiles()
    }

    // ── Delete ────────────────────────────────────────────────

    const askDelete = (item: ProjectFile) => {
      pendingDeleteId.value = item.id
      pendingDeleteIds.value = [item.id]
      pendingDeleteKind.value = 'single'
      pendingDeleteType.value = 'file'
      confirmLabel.value = item.name
      confirmOpen.value = true
    }

    const askDeleteFolder = (folder: ProjectFolder) => {
      pendingDeleteId.value = folder.id
      pendingDeleteType.value = 'folder'
      confirmLabel.value = `pasta "${folder.name}"`
      confirmOpen.value = true
    }

    const askDeleteSelected = () => {
      if (!hasSelectedFiles.value) return
      pendingDeleteId.value = ''
      pendingDeleteIds.value = [...selectedFileIds.value]
      pendingDeleteKind.value = 'selection'
      pendingDeleteType.value = 'file'
      confirmLabel.value = `${selectedFileIds.value.length} arquivo${selectedFileIds.value.length !== 1 ? 's' : ''}`
      confirmOpen.value = true
    }

    const cancelDelete = () => {
      confirmOpen.value = false
      pendingDeleteId.value = ''
      pendingDeleteIds.value = []
      pendingDeleteKind.value = 'single'
      confirmLabel.value = ''
    }

    const confirmDelete = async () => {
      if (pendingDeleteType.value === 'file' && pendingDeleteKind.value === 'selection') {
        if (!pendingDeleteIds.value.length) { cancelDelete(); return }
        bulkActionLoading.value = true
        try {
          await Promise.all(
            pendingDeleteIds.value.map((id) =>
              apiFetch(`/project-files/${encodeURIComponent(id)}`, { method: 'DELETE' })
            )
          )
          if (viewerFile.value && pendingDeleteIds.value.includes(viewerFile.value.id)) viewerFile.value = null
          if (editingId.value && pendingDeleteIds.value.includes(editingId.value)) editingId.value = ''
          selectedFileIds.value = selectedFileIds.value.filter((id) => !pendingDeleteIds.value.includes(id))
          cancelDelete()
          await refreshFiles()
        } finally {
          bulkActionLoading.value = false
        }
        return
      }
      if (!pendingDeleteId.value) { cancelDelete(); return }
      if (pendingDeleteType.value === 'folder') {
        await apiFetch(`/project-folders/${encodeURIComponent(pendingDeleteId.value)}`, { method: 'DELETE' })
        if (currentFolderId.value === pendingDeleteId.value) currentFolderId.value = null
      } else {
        await apiFetch(`/project-files/${encodeURIComponent(pendingDeleteId.value)}`, { method: 'DELETE' })
        if (viewerFile.value?.id === pendingDeleteId.value) viewerFile.value = null
        if (editingId.value === pendingDeleteId.value) editingId.value = ''
      }
      cancelDelete()
      await refreshFiles()
    }

    // ── Lifecycle ─────────────────────────────────────────────

    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && viewerFile.value) closeViewer() }

    onMounted(() => {
      refreshFiles().catch(() => { formError.value = 'Falha ao carregar arquivos.' })
      window.addEventListener('keydown', onKeyDown)
    })

    onUnmounted(() => { window.removeEventListener('keydown', onKeyDown) })

    watch(() => props.projectId, () => {
      viewerFile.value = null
      editingId.value = ''
      showCreateForm.value = false
      currentFolderId.value = null
      clearSelection()
      refreshFiles().catch(() => { formError.value = 'Falha ao carregar arquivos.' })
    })

    return {
      files, folders, currentFolderId, currentFolder, viewerFile,
      showCreateForm, uploading, formError, editingId, editingItem,
      confirmOpen, confirmLabel, creatingFolder, newFolderName, renamingFolder, renameFolderName,
      selectedFileIds, selectedFiles, hasSelectedFiles, selectedSingleFile,
      dragOverFolderId, dragOverRoot, draggingFileIds,
      showMoveModal, moveTargetFolderId, showRenameSelectionModal, renameSelectionName, bulkActionLoading,
      newFileInputRef, multiFileInputRef, editFileInputRef, folderInputRef, renameInputRef, renameSelectionInputRef,
      createForm, editForm, revisionOptions, multiFiles,
      visibleFolders, visibleFiles, gridItems,
      isFileSelected, toggleFileSelection, selectAllVisibleFiles, clearSelection,
      toggleCreateForm, toggleUploadPanel,
      triggerNewFile, triggerMultipleFiles, triggerEditFile,
      onNewFilePicked, onDropFile, onMultipleFilesPicked, onEditFilePicked,
      submitCreate, openViewer, closeViewer, openEditFromViewer,
      openEditForm, cancelEdit, submitEdit,
      enterFolder, exitFolder, startCreateFolder, submitCreateFolder,
      openMoveModal, closeMoveModal, submitMoveSelected,
      openRenameSelected, closeRenameSelection, submitRenameSelected, duplicateSelectedFiles,
      onFileDragStart, onFileDragEnd, onFolderDragOver, onFolderDragLeave, onFolderDrop, onRootDragOver, onRootDragLeave, onRootDrop,
      openRenameFolder, submitRenameFolder,
      askDelete, askDeleteFolder, askDeleteSelected, cancelDelete, confirmDelete
    }
  }
})
</script>

<style scoped lang="scss">
.fm-root {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Toolbar ── */
.fm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.fm-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fm-crumb {
  background: none;
  border: none;
  padding: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink-muted);
  cursor: pointer;
  transition: color 0.15s;

  &.fm-crumb-active { color: var(--ink); cursor: default; }
  &:not(.fm-crumb-active):hover { color: var(--accent); }
}

.fm-crumb-drop {
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--accent-light);
  color: var(--accent);
}

.fm-crumb-sep { color: var(--ink-faint); font-size: 16px; }

.fm-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fm-count { font-size: 12px; color: var(--ink-muted); }

.fm-selection-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 8px;
  border: 1px solid var(--accent-light);
  border-radius: var(--radius-lg);
  background: color-mix(in srgb, var(--accent-light) 55%, white);
}

.fm-selection-count {
  font-size: 12px;
  font-weight: 700;
  color: var(--accent);
}

/* ── Buttons ── */
.fm-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  border-radius: var(--radius);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.fm-btn-sm { padding: 5px 12px; }

.fm-btn-primary {
  background: var(--accent);
  color: #fff;
  &:hover { background: var(--accent-hover); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.fm-btn-ghost {
  background: var(--surface-3);
  color: var(--ink);
  &:hover { background: var(--surface-2); }
}

.fm-btn-danger {
  background: transparent;
  color: #c53030;
  border: 1px solid rgba(197, 48, 48, 0.4);
  &:hover { background: rgba(197, 48, 48, 0.08); }
}

/* ── Create folder bar ── */
.fm-create-folder {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--card);
  border: 1px solid var(--accent-light);
  border-radius: var(--radius-lg);
}

.fm-folder-input {
  flex: 1;
  min-width: 0;
}

/* ── Upload Panel ── */
.fm-upload-panel {
  background: var(--card);
  border: 1px solid var(--surface-3);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.fm-edit-panel {
  border-color: var(--accent-light);
}

.fm-panel-inner {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fm-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fm-panel-title { font-size: 13px; font-weight: 600; color: var(--ink); }

.fm-close-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
  border-radius: var(--radius);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--surface-3); }
}

/* ── Drop zone ── */
.fm-drop-zone {
  border: 1.5px dashed var(--surface-3);
  border-radius: var(--radius-lg);
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;

  &:hover, &--has-file {
    border-color: var(--accent);
    background: var(--accent-light);
  }
}

.fm-drop-zone--sm { padding: 12px 16px; }

.fm-drop-icon {
  font-size: 20px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}

.fm-drop-label { font-size: 13px; font-weight: 500; color: var(--ink); }
.fm-drop-hint { font-size: 11px; color: var(--ink-muted); }

/* ── Form fields ── */
.fm-form-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: end;
}

.fm-field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
}

.fm-field-xs { min-width: 72px; }

.fm-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.fm-panel-btns { display: flex; gap: 8px; }

.fm-error { color: #b3472a; font-size: 12px; font-weight: 600; margin: 0; }

/* ── Empty ── */
.fm-empty {
  text-align: center;
  padding: 40px 24px;
  color: var(--ink-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.fm-empty-icon { font-size: 44px; line-height: 1; }
.fm-empty p { margin: 0; font-size: 14px; }

/* ── Grid ── */
.fm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
}

/* ── Cards ── */
.fm-card {
  background: var(--card);
  border: 1px solid var(--surface-3);
  border-radius: var(--radius-lg);
  cursor: pointer;
  position: relative;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.1s;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    box-shadow: var(--shadow-md);
    border-color: var(--accent);
    transform: translateY(-1px);
    .fm-card-hover { opacity: 1; }
  }
}

.fm-card--selected {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 20%, transparent);
}

.fm-card--dragging {
  opacity: 0.72;
}

.fm-card--drag-over {
  border-color: var(--accent);
  background: var(--accent-light);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 18%, transparent);
}

/* ── Card Thumb ── */
.fm-card-thumb {
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-2);
  overflow: hidden;
  flex-shrink: 0;
}

.fm-select-check {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  input {
    display: none;
  }

  span {
    width: 20px;
    height: 20px;
    border-radius: 999px;
    border: 1.5px solid rgba(255, 255, 255, 0.92);
    background: rgba(17, 24, 39, 0.24);
    box-shadow: var(--shadow-sm);
    transition: background 0.15s, border-color 0.15s, transform 0.15s;
  }

  &--checked span {
    background: var(--accent);
    border-color: var(--accent);
    transform: scale(1.02);
  }

  &--checked span::after {
    content: '';
    display: block;
    width: 5px;
    height: 9px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: translate(6px, 3px) rotate(45deg);
  }
}

.fm-thumb-folder {
  background: linear-gradient(135deg, var(--accent-light) 0%, var(--surface-2) 100%);
  color: var(--accent);
}

.fm-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ── File icons ── */
.fm-file-icon {
  width: 48px;
  height: 60px;
  border-radius: 5px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 5px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 5px;
    opacity: 0.14;
    background: currentColor;
  }
}

.fm-file-icon-sm {
  width: 30px;
  height: 38px;
  border-radius: 4px;
  padding-bottom: 3px;
}

.fm-icon-ext {
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
}

.fm-icon-pdf { color: #e03131; }
.fm-icon-jpg, .fm-icon-jpeg, .fm-icon-png, .fm-icon-gif,
.fm-icon-webp, .fm-icon-bmp, .fm-icon-svg { color: #2f9e44; }
.fm-icon-dwg { color: var(--accent); }
.fm-file-icon:not([class*="fm-icon-pdf"]):not([class*="fm-icon-jp"]):not([class*="fm-icon-png"]):not([class*="fm-icon-gif"]):not([class*="fm-icon-webp"]):not([class*="fm-icon-bmp"]):not([class*="fm-icon-svg"]):not([class*="fm-icon-dwg"]) {
  color: var(--ink-muted);
}

/* ── Card info ── */
.fm-card-info {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fm-card-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fm-card-meta {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fm-badge-rev {
  font-size: 10px;
  font-weight: 600;
  background: var(--accent-light);
  color: var(--accent);
  padding: 1px 5px;
  border-radius: 3px;
}

.fm-card-type { font-size: 10px; color: var(--ink-faint); }

/* ── Hover actions ── */
.fm-card-hover {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  opacity: 0;
  transition: opacity 0.15s;
}

.fm-action-btn {
  width: 26px;
  height: 26px;
  border-radius: var(--radius);
  border: none;
  background: var(--surface);
  color: var(--ink-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
  transition: color 0.15s, background 0.15s;

  &:hover { background: var(--accent); color: #fff; }
  &.fm-action-danger:hover { background: #e03131; color: #fff; }
}

/* ── Rename modal ── */
.fm-rename-modal {
  background: var(--card);
  border-radius: var(--radius-lg);
  padding: 20px;
  width: 320px;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── Viewer overlay ── */
.fm-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.fm-viewer-modal {
  background: var(--card);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 960px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fm-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid var(--surface-3);
  gap: 12px;
  flex-shrink: 0;
}

.fm-viewer-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.fm-viewer-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fm-viewer-meta { font-size: 11px; color: var(--ink-muted); margin: 0; }

.fm-viewer-controls { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

.fm-viewer-close {
  width: 30px;
  height: 30px;
  border: none;
  background: var(--surface-3);
  color: var(--ink);
  border-radius: var(--radius);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--surface-2); }
}

.fm-viewer-body {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.fm-iframe {
  flex: 1;
  width: 100%;
  min-height: 500px;
  border: none;
}

.fm-img-viewer {
  width: 100%;
  max-height: 580px;
  object-fit: contain;
  display: block;
}

.fm-dwg-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px;
  color: var(--ink-muted);
}

.fm-dwg-big { font-size: 48px; line-height: 1; }

/* ── Transitions ── */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.viewer-fade-enter-active,
.viewer-fade-leave-active { transition: opacity 0.2s ease; }
.viewer-fade-enter-from,
.viewer-fade-leave-to { opacity: 0; }

/* ── Misc ── */
.hidden-input { display: none; }

/* ── Responsive ── */
@media (max-width: 600px) {
  .fm-grid { grid-template-columns: repeat(auto-fill, minmax(105px, 1fr)); }
  .fm-form-row { grid-template-columns: 1fr; }
  .fm-viewer-overlay { padding: 0; align-items: flex-end; }
  .fm-viewer-modal { border-radius: var(--radius-lg) var(--radius-lg) 0 0; max-height: 92vh; }
}
</style>
